import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { DateSerializer, UrlFormatter } from 'shared';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { selectSingleProperty } from 'account-hybrid/store';
import { GuestsV1ApiService } from 'apps/account/src/account-shared/services/guests-v1-api.service';
import { LayersApiService } from 'apps/account/src/account-shared/services/layers-api.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { PropertyApiService } from 'apps/account/src/account-shared/services/property-api.service';
import { V1HttpShimService } from 'apps/account/src/account-shared/services/v1-http-shim.service';
import { AdvocacyGroupState } from './advocacy-group.reducers';
import { selectAdvocacyGroupFeature } from './advocacy-group.selectors';

/**
 * `$ngRedux`-shaped facade over the native NgRx `advocacyGroup` feature.
 *
 * WS2 strangle: the five native advocacy-group components used to read/dispatch
 * the AngularJS ng-redux store via `$injector.get('$ngRedux')`. This facade
 * exposes the SAME `getState()` / `dispatch()` / `subscribe()` shape so those
 * components keep their `syncFromState()` + dispatch code VERBATIM — only the
 * store source swaps (one line per component). The advocacy view + apiData
 * slices now live in the NgRx store; the existing redux-thunks are reused
 * unchanged via the thunk-dispatch adapter below (it reimplements redux-thunk:
 * a dispatched function is invoked with (dispatch, getState, extraArgs); a plain
 * action is forwarded to the NgRx store, where the reused reducers handle it).
 *
 * `getState()` reshapes the flat feature state back into `{ views, apiData }` so
 * the components' `state.views.X` / `state.apiData.X` reads are unchanged. The
 * only `ui` value any advocacy component reads is `ui.context.singleProperty`
 * (audiences only), now sourced from the NATIVE NgRx `context` store via
 * `selectSingleProperty` — so the facade no longer reads the ng-redux store at
 * all. Since WS5 #10 Stage A inc 2f the thunk extra-args resolve natively too —
 * this facade has NO AngularJS dependency left.
 */
@Injectable()
export class AdvocacyNgReduxFacade implements OnDestroy {
  // WS5 #10 Stage A inc 2f: the thunk extra-args resolve FULLY NATIVELY — the
  // facade no longer touches the AngularJS `$injector` at all. The old 17-name
  // list was a verbatim copy of the ng-redux store's `thunk.withExtraArgument`
  // wiring (angularjs.module.ts); an account-v2-wide census of every thunk's
  // extras destructuring shows only these names are EVER read ($rootScope/Admin/
  // localStorage/User/IdentityService/SessionService/Account/ftCache never are):
  // - $http → V1HttpShimService ($http-shaped {data,status} Promise adapter)
  // - dateSerializer / urlFormatter → the byte-identical `shared` static ports
  //   (thunks call only `urlFormatter.format`; combineUrlPath kept for safety)
  // - Layers/PhotoContest/Property/Guests → the native HttpClient API ports
  // - SpacesService → the native service (the AngularJS name was its downgrade)
  // - environment → ENVIRONMENT_TOKEN (the SAME object downgradeForAngularJS
  //   feeds the AngularJS `environment` provider — apiBaseUrl value-identical)
  private readonly httpShim = inject(V1HttpShimService);
  private readonly spacesService = inject(SpacesService);
  private readonly environment = inject(ENVIRONMENT_TOKEN) as Environment;
  private readonly extraArgs: Record<string, any> = {};
  private current: AdvocacyGroupState | undefined;
  // The single-property context — now sourced from the NATIVE NgRx `context` store
  // (was the ng-redux `ui.context.singleProperty` read-through). Both stores are set
  // to the same currentSpace.id (property UUID) at boot, so this is value-preserving
  // on the Property/Mixed spaces where the audiences leaf actually renders.
  private singleProperty: string | null = null;
  private readonly featureSub: Subscription;
  private readonly singlePropertySub: Subscription;

  constructor(
    private readonly store: Store,
    private readonly layersApi: LayersApiService,
    private readonly photoContestApi: PhotoContestApiService,
    private readonly propertyApi: PropertyApiService,
    private readonly guestsApi: GuestsV1ApiService,
  ) {
    Object.assign(this.extraArgs, {
      $http: this.httpShim,
      dateSerializer: (date: any) => DateSerializer.serialize(date),
      Layers: this.layersApi,
      urlFormatter: {
        format: (url: string, params?: any, appendSearch?: boolean) => UrlFormatter.format(url, params, appendSearch),
        combineUrlPath: (url: string, ...paths: string[]) => UrlFormatter.combineUrlPath(url, ...paths),
      },
      PhotoContest: this.photoContestApi,
      Property: this.propertyApi,
      SpacesService: this.spacesService,
      Guests: this.guestsApi,
      environment: this.environment,
    });
    // Keep a synchronous snapshot of the feature state (NgRx emits synchronously
    // to this subscription on dispatch, before component subscriptions fire).
    this.featureSub = this.store.select(selectAdvocacyGroupFeature).subscribe((s) => {
      if (s) this.current = s;
    });
    this.singlePropertySub = this.store
      .select(selectSingleProperty)
      .subscribe((v) => (this.singleProperty = v ?? null));
  }

  ngOnDestroy(): void {
    this.featureSub?.unsubscribe();
    this.singlePropertySub?.unsubscribe();
  }

  getState = (): any => {
    const s: any = this.current || {};
    return {
      views: {
        guestsJourneys: s.guestsJourneys,
        bigPicture: s.bigPicture,
        audiences: s.audiences,
        contests: s.contests,
      },
      apiData: {
        propertyGuestsJourneys: s.propertyGuestsJourneys,
        propertyAdvocacyOverview: s.propertyAdvocacyOverview,
        propertyAdvocacyImpact: s.propertyAdvocacyImpact,
        propertyPlatformData: s.propertyPlatformData,
        prestay: s.prestay,
        poststay: s.poststay,
        photoAdvocacy: s.photoAdvocacy,
      },
      // The shared single-property context — now read from the native NgRx
      // `context` store (audiences reads only `ui.context.singleProperty`). This
      // dropped the facade's last ng-redux STORE-state read; the thunk
      // extra-args are fully native too (inc 2f), so no AngularJS remains here.
      ui: { context: { singleProperty: this.singleProperty } },
    };
  };

  // redux-thunk-compatible dispatch: a function is a thunk → invoke with
  // (dispatch, getState, extraArgs); a plain action → forward to the NgRx store.
  dispatch = (action: any): any =>
    typeof action === 'function'
      ? action(this.dispatch, this.getState, this.extraArgs)
      : this.store.dispatch(action);

  subscribe = (listener: () => void): (() => void) => {
    const sub = this.store.select(selectAdvocacyGroupFeature).subscribe(() => listener());
    return () => sub.unsubscribe();
  };
}
